import { Helmet } from "react-helmet-async";
import { SOCIAL_IDENTITY } from "@/lib/entity-jsonld";
import { resolveOgImage } from "@/content/og-images";
import { breadcrumbJsonLd } from "@/lib/breadcrumbs";

interface PageMetaProps {
  title: string;
  description: string;
  path: string;
  /** Optional JSON-LD object (or @graph) injected as a <script type="application/ld+json">. */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  /** When true, emits <meta name="robots" content="noindex,nofollow"> — for internal pages. */
  noindex?: boolean;
  /** Absolute or site-relative path to a 1200x630 social share image. */
  image?: string;
  /** Descriptive alt text for the social share image (accessibility + Twitter). */
  imageAlt?: string;
  /** OpenGraph type: "website" (default), "article", "book", "profile", etc. */
  ogType?: "website" | "article" | "book" | "profile" | "product";
  /** Optional RSS feed for this page: emits <link rel="alternate" type="application/rss+xml">. */
  feed?: { url: string; title: string };
}

const SITE_URL = "https://drsullyspeaks.com";
const SITE_NAME = SOCIAL_IDENTITY.siteName;
const TWITTER_HANDLE = SOCIAL_IDENTITY.twitterHandle;

const toAbsolute = (src: string) =>
  src.startsWith("http") ? src : `${SITE_URL}${src.startsWith("/") ? "" : "/"}${src}`;

/**
 * Normalize a route path into a canonical URL:
 *   - always lowercase (avoids /Ebooks vs /ebooks duplicates)
 *   - strip query string and hash fragment
 *   - collapse a trailing slash except at the site root
 * Keeps canonical / og:url identical for every variant a crawler can reach.
 */
const canonicalize = (path: string) => {
  const clean = path.split("?")[0].split("#")[0].toLowerCase();
  const trimmed = clean.length > 1 && clean.endsWith("/") ? clean.slice(0, -1) : clean;
  return `${SITE_URL}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;
};

export const PageMeta = ({
  title,
  description,
  path,
  jsonLd,
  noindex,
  image,
  imageAlt,
  ogType = "website",
  feed,
}: PageMetaProps) => {
  const url = canonicalize(path);
  // Route-specific share card unless the page passes an explicit override.
  const routeImage = resolveOgImage(path);
  const imageUrl = toAbsolute(image ?? routeImage.src);
  const alt = imageAlt ?? (image ? title : routeImage.alt);
  // Sitewide breadcrumbs: injected automatically for every mapped route unless
  // the page already ships its own BreadcrumbList (avoids duplicate trails).
  const graph = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const flattened = graph.flatMap((node) =>
    Array.isArray((node as { "@graph"?: unknown })["@graph"])
      ? ((node as { "@graph": Record<string, unknown>[] })["@graph"])
      : [node],
  );
  const hasBreadcrumb = flattened.some((n) => n?.["@type"] === "BreadcrumbList");
  const crumbNode = noindex || hasBreadcrumb ? null : breadcrumbJsonLd(path);
  const jsonLdOut = crumbNode
    ? { "@context": "https://schema.org", "@graph": [...flattened, crumbNode] }
    : jsonLd;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {feed && <link rel="alternate" type="application/rss+xml" title={feed.title} href={feed.url} />}
      {/* Explicit on every route: indexable pages opt into rich snippets/large image
          previews, internal pages are fully excluded from index + link graph. */}
      <meta
        name="robots"
        content={
          noindex
            ? "noindex,nofollow"
            : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
        }
      />
      <meta
        name="googlebot"
        content={
          noindex
            ? "noindex,nofollow"
            : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
        }
      />


      {/* OpenGraph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:type" content={imageUrl.endsWith(".png") ? "image/png" : "image/jpeg"} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={alt} />

      {/* Author / profile identity — kept identical to index.html so every
          platform attributes the page to the same person + page. */}
      <meta name="author" content={SOCIAL_IDENTITY.authorName} />
      <meta property="article:publisher" content={SOCIAL_IDENTITY.facebookPage} />
      <meta property="article:author" content={SOCIAL_IDENTITY.facebookProfile} />
      <meta property="profile:username" content={TWITTER_HANDLE.replace("@", "")} />



      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={alt} />

      {jsonLdOut && (
        <script type="application/ld+json">{JSON.stringify(jsonLdOut)}</script>
      )}
    </Helmet>
  );
};

